import { oc } from "@orpc/contract";
import { z } from "zod";
import { IntegrationBaseSchema, IntegrationKey } from "./base";

/**
 * Jira Integration Settings Schema
 *
 * `apiToken` is optional on updates — when omitted the backend preserves the
 * existing encrypted value. It is always required on first save.
 *
 * Targets Jira Cloud. Auth uses HTTP Basic with `serviceAccountEmail` as the
 * username and `apiToken` as the password (Jira Cloud convention).
 */
export const JiraIntegrationSettingsSchema = z.object({
  // Restrict to Jira Cloud (`https://<tenant>.atlassian.net`) — anything
  // else is either misconfiguration or an attempt to point our
  // server-side fetch at an unrelated endpoint with the org's Jira
  // credentials. The handler reads this URL and hits it with HTTP Basic
  // auth, so a loose validation here is a real SSRF surface.
  baseUrl: z
    .string()
    .url()
    .refine(
      (value) => {
        try {
          const u = new URL(value);
          return (
            u.protocol === "https:" && u.hostname.endsWith(".atlassian.net")
          );
        } catch {
          return false;
        }
      },
      { message: "baseUrl must be an https://<tenant>.atlassian.net URL" },
    ),
  serviceAccountEmail: z.string().email(),
  apiToken: z.string().optional(),
  defaultProjectKey: z.string().min(1),
});

export type JiraIntegrationSettings = z.infer<
  typeof JiraIntegrationSettingsSchema
>;

export const JiraIntegrationSettingsSchemaWithoutBase = z.object({
  key: z.literal(IntegrationKey.Jira),
  settings: JiraIntegrationSettingsSchema,
});

export const JiraIntegrationSchema = IntegrationBaseSchema.merge(
  JiraIntegrationSettingsSchemaWithoutBase,
);

export type JiraIntegration = z.infer<typeof JiraIntegrationSchema>;

// ── Contracts ──

const testConnection = oc
  .input(z.object({ integrationId: z.string().uuid() }))
  .output(
    z.object({
      success: z.boolean(),
      message: z.string(),
    }),
  )
  .route({
    summary: "Test Jira connection",
    description:
      "Calls Jira with the integration's stored service account and API token and returns whether the connection succeeded and a message.",
  });

export const jiraContracts = {
  testConnection,
} as const;

export type JiraContracts = typeof jiraContracts;
